import { HttpErrorResponse } from '@angular/common/http'
// amgular
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { sharedAnimations } from 'src/app/modules/shared/shared.animations'
import userIconColorMapJSON from 'src/assets/user-management/user-icon-color-map-array.json'
// app
import { environment } from '@environments/environment'
import { EnvironmentInterface } from '@environments/environment.interface'
import { ApplicationLifecycleService } from '@services/application-lifecycle.service'
import { BillingService } from '@services/billing/billing.service'
import { NotificationService } from '@services/notification.service'
import { UiService } from '@services/ui/ui.service'
import { PCOverlayConfig } from '@shared/type-definitions/types'
import { Subscription, TimeoutError } from 'rxjs'
import { filter } from 'rxjs/operators'
import { PcStatusOverlayService } from 'src/app/modules/shared/services/pc-status-overlay/pc-status-overlay.service'
import { AppService } from '../../../../app.service'
import { IamStore } from '../../stores/iam.store'
import { NavigationService } from '../navigation/navigation.service'
import { IIconColor } from './interfaces/i-icon-color'
import { profileLogoHoverAnimation } from './proficloud-shell.animation'

@Component({
  selector: 'app-proficloud-shell',
  templateUrl: './proficloud-shell.component.html',
  styleUrls: ['./proficloud-shell.component.scss', '../../../shared/styles/shared-styles.scss'],
  animations: [
    sharedAnimations.widthSlide,
    sharedAnimations.heightSlide,
    sharedAnimations.scaleIn,
    sharedAnimations.scaleOut,
    sharedAnimations.widthSlideTrueFalse,
    profileLogoHoverAnimation,
  ],
})
export class ProficloudShellComponent implements OnInit, OnDestroy {
  @ViewChild('content')
  contentContainer: ElementRef

  env: EnvironmentInterface = environment

  menuVisible = false

  settingItem = {
    name: 'Settings',
    icon: 'gear',
  }

  // Create new organization
  createOrgDialog: PCOverlayConfig = {
    title: 'Your Organization',
  }

  showCreateOrg = false

  createOrgNameControl = new UntypedFormControl('', [Validators.required, Validators.maxLength(256), Validators.pattern('^[-_.@, A-Za-z0-9]+$')])

  createOrganisationForm = new UntypedFormGroup({ name: this.createOrgNameControl })

  firstNameColor: IIconColor = { id: 'A', color: '#FFFFFF' }

  lastNameColor: IIconColor = { id: 'A', color: '#FFFFFF' }

  profileLogoHovered: boolean = false

  private organisationsSubscription: Subscription
  private iamStoreInitSubscription: Subscription
  private orgSwitchBegunSubscription: Subscription
  private orgSwitchedSubscription: Subscription
  private lifecycleErrorsSubscription: Subscription

  constructor(
    public notificationService: NotificationService,
    public app: AppService,
    public billing: BillingService,
    public ui: UiService,
    private statusOverlay: PcStatusOverlayService,
    public navigation: NavigationService,
    public iamStore: IamStore,
    public applicationLifecycle: ApplicationLifecycleService
  ) {}

  ngOnInit() {
    // Any time a user is fetched we need to generate the profile avatar
    this.iamStoreInitSubscription = this.iamStore.storeInitialised$.pipe(filter((errored) => errored !== undefined)).subscribe(() => {
      this.generateProfileAvatar()

      // New experimental notifications backend - this should only be live on dev for now
      if (this.app.environment.frontend !== 'production') {
        this.notificationService.setupMessagingBackend()
      }
    })

    // Organisation switch loading status overlay
    // Note: this is the only place this should happen and is for all modules/components
    this.orgSwitchBegunSubscription = this.applicationLifecycle.organisationSwitchBegun$.subscribe(() => {
      this.statusOverlay.queueLoadingStatus$.next({ key: 'orgSwitch', config: this.ui.statusOverlays.switchOrganisationBusy })
    })

    // Organisation switch resolution status overlays
    this.orgSwitchedSubscription = this.applicationLifecycle.organisationSwitched$.subscribe((res) => {
      if (res) {
        this.statusOverlay.resolveLoadingStatus$.next({ key: 'orgSwitch', config: this.ui.statusOverlays.switchOrganisationSuccess })
      } else {
        this.statusOverlay.resolveLoadingStatus$.next({ key: 'orgSwitch', config: this.ui.statusOverlays.switchOrganisationError })
      }
    })

    // General error handling for the application lifecycle
    this.lifecycleErrorsSubscription = this.applicationLifecycle.errors$.subscribe((error) => {
      if (error.code === 'SWITCH_ORGANISATION') {
        if (error.error instanceof TimeoutError) {
          this.statusOverlay.showStatus(this.ui.statusOverlays.switchOrganisationStuckError)
        } else {
          this.statusOverlay.showStatus(this.ui.statusOverlays.switchOrganisationError)
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.organisationsSubscription?.unsubscribe()
    this.iamStoreInitSubscription?.unsubscribe()
    this.orgSwitchBegunSubscription?.unsubscribe()
    this.orgSwitchedSubscription?.unsubscribe()
    this.lifecycleErrorsSubscription?.unsubscribe()
  }

  public createOrganization() {
    this.statusOverlay.showStatus(this.ui.statusOverlays.createOrganizationBusy)

    const name = this.createOrganisationForm.get('name')?.value
    if (!name) {
      console.warn('missing organisation name')
      return
    }

    this.iamStore.createIamOrganisation(name).subscribe({
      next: (newOrganisation) => {
        this.showCreateOrg = false
        this.statusOverlay.showStatus(this.ui.statusOverlays.createOrganizationSuccess)

        // Switch to newly created organisation
        this.iamStore.switchOrganisation(newOrganisation.data.organizationId)
      },
      error: (err: HttpErrorResponse) => {
        console.log(err)
        this.statusOverlay.showStatus(this.ui.statusOverlays.createOrganizationError)
      },
    })
  }

  private generateProfileAvatar() {
    // Get capitalized first characters of user name
    // Fallback first two characters of email
    let firstLetterFirstName = this.iamStore.currentUser?.data.firstName.charAt(0).toUpperCase()
    let firstLetterLastName = this.iamStore.currentUser?.data.lastName.charAt(0).toUpperCase()

    if (!firstLetterFirstName || !firstLetterLastName) {
      firstLetterFirstName = this.iamStore.currentUser?.data.email.charAt(0).toUpperCase() || 'unknown'
      firstLetterLastName = this.iamStore.currentUser?.data.email.charAt(1).toUpperCase() || 'unknown'
    }

    // Get color list and assign colors based on name
    // Fallback: color based on email
    const colorMap: string[] = userIconColorMapJSON.colors
    this.firstNameColor = {
      id: firstLetterFirstName,
      color: colorMap[firstLetterFirstName.charCodeAt(0) % colorMap.length],
    }
    this.lastNameColor = {
      id: firstLetterLastName,
      color: colorMap[firstLetterLastName.charCodeAt(0) % colorMap.length],
    }
  }
}
